import React, { useState } from "react";
import { useSelector } from "react-redux";
import ProgressLoader from "../../ProgressLoader";
import CircularProgress from "../../CircularProgress";
import { DownloadFileIcon, Playbtn2, RetryIcon, VideoIcon3 } from "../../../../../assets/images";
import { captionLink, getThumbBase64URL, millisToMinutesAndSeconds } from "../../../../../Helpers/Utility";
import Translate from "./Translate";
import { getLocalUserDetails } from "../../../../../Helpers/Chat/User";
import PropTypes from "prop-types";
import "./GroupedImageMessages.scss";
import { useMessageProcessing } from "./useMessageProcessing";
const HtmlToReactParser = require('html-to-react').Parser;
const htmlToReactParser = new HtmlToReactParser();

const VideoComponent = (props = {}) => {
  const globalStore = useSelector((state) => state);
  const {
    thumbURL,
    handleMediaShow,
    isSender = true,
    uploadStatus = 0,
    messageObject = {},
    videoFileDownloadOnclick,
    imageHeightAdjust = false,
    pageType,
    isVideoDownloaded = false
  } = props;

  const { mediaDownloadData } = globalStore || {};

  const {
    msgId = "",
    msgBody: {
      translatedMessage = "",
      media: { caption = "", file_url = "", duration, thumb_image = "", webWidth, webHeight, file_size, local_path = "" } = {},
      mentionedUsersIds = []
    } = {}
  } = messageObject;

  const vcardData = getLocalUserDetails();

  const { messageRef } = useMessageProcessing({
    messageObject,
    isSender,
    vCardData: { data: vcardData },
    htmlToReactParser
  });

  const [thumbImgSrc] = useState(thumbURL || getThumbBase64URL(thumb_image));
  const [downloadStatus, setDownloadStatus] = useState(''); // '', 'downloading', 'cancelled', 'failed'
  const [isDownloading, setIsDownloading] = useState(false);

  const isTranslated = () =>
    isSender && pageType === "conversation" && translatedMessage && Object.keys(translatedMessage).length;

  // Format file size
  const formatSize = (bytes) => {
    if (!bytes) return "512 KB"; // Default estimate for video
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Handle download click
  const handleDownloadClick = async (e) => {
    e.stopPropagation();
    setIsDownloading(true);
    setDownloadStatus('downloading');
    
    if (videoFileDownloadOnclick) {
      videoFileDownloadOnclick({ target: { id: file_url } }, () => {
        // Reset after download completes
        setTimeout(() => {
          setIsDownloading(false);
          setDownloadStatus(''); // Clear status after successful download
        }, 1000);
      });
    }
  };

  // Handle cancel download
  const handleCancelDownload = (e) => {
    e.stopPropagation();
    setIsDownloading(false);
    setDownloadStatus('cancelled');
  };

  // Handle retry download
  const handleRetryDownload = (e) => {
    e.stopPropagation();
    setDownloadStatus('');
    handleDownloadClick(e);
  };


  return (
    <>
      <div className="image-wrapper">
      <div
          className="image-message"
          role="button"
          tabIndex={isVideoDownloaded || uploadStatus === 2 ? 0 : -1}
          onClick={(e) => {
            if (isVideoDownloaded || uploadStatus === 2) {
              handleMediaShow?.(e);
            }
          }}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && (isVideoDownloaded || uploadStatus === 2)) {
              e.preventDefault();
              handleMediaShow?.(e);
            }
          }}
        >
          <img className="webchat-conver-image" src={thumbImgSrc} alt="chat-img" />

          <div className="image-overlay">
            <span className="duration">
              <i>
                <VideoIcon3 />
              </i>
              <span className="video-duration">{millisToMinutesAndSeconds(duration)}</span>
            </span>
            {msgId === mediaDownloadData?.downloadingStatus[msgId]?.downloadMediaMsgId && mediaDownloadData?.downloadingStatus[msgId]?.downloading === true ? (
              <i></i>
            ) : (uploadStatus === 2 || isVideoDownloaded) && uploadStatus !== 3 ? (
              <i className="playbtn">
                <Playbtn2 />
              </i>
            ) : null}
          </div>

          {/* Download overlay states for non-downloaded videos */}
          {!isVideoDownloaded && uploadStatus === 3 && (
            <div className="common-download-overlay">
              {isDownloading ? (
                // Show cancel button and progress during download
                <div className="download-progress-container">
                  <div className="download-progress-info">
                    <div className="download-progress-wrapper">
                      <CircularProgress 
                        progress={50}
                        size={56}
                        strokeWidth={4}
                      />
                    </div>
                    <button 
                      className="cancel-button"
                      onClick={handleCancelDownload}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleCancelDownload(e);
                        }
                      }}
                      aria-label="Cancel download"
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M6 6L18 18M6 18L18 6" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
              ) : downloadStatus === 'cancelled' || downloadStatus === 'failed' ? (
                // Show retry button
                <div 
                  className="retry-download-button"
                  onClick={handleRetryDownload}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleRetryDownload(e);
                    }
                  }}
                >
                  <RetryIcon/>
                </div>
              ) : (
                // Show initial download button with size
                <div 
                  className="initial-download-button"
                  onClick={handleDownloadClick}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleDownloadClick(e);
                    }
                  }}
                >
                  <div className="download-info-wrapper">
                    <DownloadFileIcon style={{ width: "16px", height: "16px" }}/>
                    <div className="download-size-details">
                      <span className="size-large">{formatSize(file_size)}</span>
                      <span className="items-small">1 item</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Loading spinner during download (uploadStatus 4) */}
          {uploadStatus === 4 && (
            <div className="single-media-download-overlay" style={{ zIndex: 3 }}>
              <div className="single-spinner-container">
                <div className="single-spinner"></div>
              </div>
            </div>
          )}
        </div>
        <ProgressLoader
          msgId={msgId}
          isSender={isSender}
          uploadStatus={uploadStatus}
          imgFileDownloadOnclick={videoFileDownloadOnclick}
          file_url={file_url}
        />
      </div>
      <div className="image-caption" ref={messageRef}>
        {caption !== "" && (
          <span>
            <span>{captionLink(caption,mentionedUsersIds,vcardData.userId,messageObject.chatType)}</span>
            {!!isTranslated() && <Translate tMessage={translatedMessage} />}
          </span>
        )}
      </div>
    </>
  );
};

export default VideoComponent;

VideoComponent.propTypes = {
  handleMediaShow: PropTypes.func,
  isSender: PropTypes.bool,
  uploadStatus: PropTypes.number,
  messageObject: PropTypes.object,
  videoFileDownloadOnclick: PropTypes.func,
  imageHeightAdjust: PropTypes.bool,
  pageType: PropTypes.string,
  thumbURL: PropTypes.string,
  isVideoDownloaded: PropTypes.bool
};